import type { MonthGroup } from '@/cmsBackend/transformers/types/featureAnnouncement';
import {
  CONTAINER_OPTIONS,
  MODULE_FILL_TYPE_OPTIONS,
  MODULE_FLOW_DIRECTION_OPTIONS,
} from '@/common/constants';
import { replaceTemplateTokens } from '@/common/helpers/templateTokens';
import { useModuleStyles } from '@/common/hooks/useModuleStyles';
import { useViewportReveal } from '@/common/hooks/useViewportReveal';
import { AnnouncementCard } from '@/components/modules/AnnouncementCard';
import classNames from 'classnames';
import React from 'react';
import styles from './MonthGroupSection.module.scss';

interface ViewportRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  skipReveal?: boolean;
}

const ViewportReveal: React.FC<ViewportRevealProps> = ({
  children,
  className,
  delay = 0,
  skipReveal = false,
}) => {
  const { ref, hasEnteredViewport, shouldHideUntilRevealed } =
    useViewportReveal<HTMLDivElement>(skipReveal);

  return (
    <div
      ref={ref}
      className={classNames(className, {
        [styles.revealHidden]: shouldHideUntilRevealed && !hasEnteredViewport,
        [styles.revealVisible]: shouldHideUntilRevealed && hasEnteredViewport,
      })}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

export interface MonthGroupSectionProps {
  monthGroup: MonthGroup;
  heading: string;
  /** Skips the scroll-reveal animation — set while this section is the
   * target of a programmatic scroll (e.g. a chip click), so it doesn't
   * render invisible for a moment while the scroll jump lands on it. */
  skipRevealAnimation?: boolean;
}

export const MonthGroupSection: React.FC<MonthGroupSectionProps> = ({
  monthGroup,
  heading,
  skipRevealAnimation = false,
}) => {
  const { sectionStyles, moduleBaseStyles } = useModuleStyles({
    container: CONTAINER_OPTIONS.UNCONTAINED,
    fillType: MODULE_FILL_TYPE_OPTIONS.BASE,
    flowDirection: MODULE_FLOW_DIRECTION_OPTIONS.COLUMN,
    moduleClassName: 'module-month-group',
    styles,
  });

  return (
    <section
      id={`month-${monthGroup.key}`}
      className={sectionStyles}
      data-month-key={monthGroup.key}
    >
      <div className={moduleBaseStyles}>
        <ViewportReveal className={styles.section__header} skipReveal={skipRevealAnimation}>
          <p className={styles.section__monthLabel}>{monthGroup.label}</p>
          <span className={styles.section__badgeGreen}>
            {replaceTemplateTokens(heading, {
              count: monthGroup.count,
              month: monthGroup.monthName,
              year: monthGroup.year,
            })}
          </span>
        </ViewportReveal>
        <div className={styles.section__cards}>
          {monthGroup.majorAnnouncements.map((card, index) => (
            <ViewportReveal key={card.id} delay={index * 80} skipReveal={skipRevealAnimation}>
              <AnnouncementCard card={card} />
            </ViewportReveal>
          ))}
        </div>
      </div>
    </section>
  );
};
